import { Component, OnDestroy, afterNextRender, input, model, inject } from "@angular/core";
import { Router, RouterLink } from "@angular/router";
import { FirstLoginWizard } from "@app/shared/_components/first-login-wizard/first-login-wizard.interface";
import { catchError, finalize, map, mergeMap } from "rxjs/operators";
import { throwError } from "rxjs";
import { SettingsService } from "@app/features/settings/settings.service";
import { MessageService, PrimeTemplate } from "primeng/api";
import { TranslateService } from "@app/shared/_services/translate.service";
import { UntypedFormBuilder, UntypedFormGroup, FormsModule, ReactiveFormsModule } from "@angular/forms";
import { trackingToolsInitialValue } from "@app/features/settings/settings.model";
import { UserService } from "@app/shared/_services/user.service";
import { User } from "@app/shared/_interfaces/user";
import { CookieService } from "ngx-cookie-service";
import { DialogModule } from "primeng/dialog";
import { ButtonDirective } from "primeng/button";
import { TableModule } from "primeng/table";
import { InputTextModule } from "primeng/inputtext";
import { MessageModule } from "primeng/message";
import { TranslatePipe } from "../../_pipes/translate.pipe";

@Component({
    selector: "first-login-wizard",
    templateUrl: `first-login-wizard.component.html`,
    styleUrls: ["./first-login-wizard.component.scss"],
    imports: [DialogModule, PrimeTemplate, FormsModule, ReactiveFormsModule, RouterLink, ButtonDirective, TableModule, InputTextModule, MessageModule, TranslatePipe]
})
export class FirstLoginWizardComponent implements OnDestroy {
  private _settingsService = inject(SettingsService);
  private _messageService = inject(MessageService);
  private _translateService = inject(TranslateService);
  private _cookieService = inject(CookieService);
  private _formBuilder = inject(UntypedFormBuilder);
  private _userService = inject(UserService);
  private _router = inject(Router);

  readonly visible = model<boolean>(false);
  readonly profileCompleted = input(false);
  readonly showWizardSteps = input(true);
  trackingGroup: UntypedFormGroup;

  steps: FirstLoginWizard.Step[] = [];

  loadingSave: boolean;
  isEuropean: boolean;
  generatedApiKey: User.ConsumerKeyItemWithSecret | null = null;
  loadingApiKeyGeneration: boolean = false;
  purchaseUrl: string = "/myaccount/purchase";
  hasPurchasePreselected = false;
  private _redirectTimeout: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    afterNextRender(() => {
      this.initializeWizard();
    });
  }

  private initializeWizard() {
    // Step 0 (profile-completed confirmation) is prepended only when profileCompleted=true
    const profileCompleted = this.profileCompleted();
    if (profileCompleted) {
      this._cookieService.delete("kc-profile-completed");
      this.steps.push({ active: true });

      const keycloakId = this._userService.getInfoValue("keycloak_id") as string;
      if (keycloakId) {
        this._userService.getKeycloakProfile(keycloakId).subscribe(({ user }) => {
          const productId = user?.attributes?.product_id?.[0];
          const billingcycle = user?.attributes?.billingcycle?.[0];
          if (productId && billingcycle) {
            this.purchaseUrl = `/myaccount/purchase/${billingcycle}/${productId}`;
            this.hasPurchasePreselected = true;
            this._redirectTimeout = setTimeout(() => {
              this._router.navigate([this.purchaseUrl]);
            }, 5000);
          }
        });
      }
    }
    // Regular wizard steps (only if needed)
    if (this.showWizardSteps()) {
      this.steps.push({ active: !profileCompleted }, { active: false });
    }

    this.trackingGroup = this._formBuilder.group(trackingToolsInitialValue);
    this._settingsService.tools.subscribe((tools) => {
      tools
        .filter((tool) => tool.id in trackingToolsInitialValue)
        .forEach((tool) => {
          this.trackingGroup.patchValue({ [tool.id]: tool.enabled });
        });
    });

    this._settingsService.getTools();
    this.isEuropean = this._userService.getInfoValue("is_european") as boolean;
  }

  get isProfileCompletedStep(): boolean {
    return this.profileCompleted() && this.curStepIndex === 0;
  }

  get curStepIndex(): number {
    return this.steps.findIndex((step) => step.active);
  }

  get curStep(): FirstLoginWizard.Step {
    return this.steps[this.curStepIndex];
  }

  get curStepTitle() {
    if (this.isProfileCompletedStep) {
      return "";
    }
    const offset = this.profileCompleted() ? 1 : 0;
    const wizardStepIndex = this.curStepIndex - offset + 1;
    const wizardStepsTotal = this.steps.length - offset;
    const title = this._translateService.translate("first_login_step", {
      n: wizardStepIndex,
    });
    return `${title} / ${wizardStepsTotal}`;
  }

  private goTo(stepIndex: number) {
    this.steps[this.curStepIndex].active = false;
    this.steps[stepIndex].active = true;
  }

  goBack() {
    this.goTo(this.curStepIndex - 1);
  }

  goNext() {
    this.goTo(this.curStepIndex + 1);
  }

  get isFirstStep(): boolean {
    return this.curStepIndex === 0;
  }

  get isLastStep(): boolean {
    return this.curStepIndex === this.steps.length - 1;
  }

  ngOnDestroy() {
    if (this._redirectTimeout) {
      clearTimeout(this._redirectTimeout);
    }
  }

  stayFree() {
    if (this._redirectTimeout) {
      clearTimeout(this._redirectTimeout);
      this._redirectTimeout = null;
    }
    if (this.showWizardSteps()) {
      this.goTo(1);
    } else {
      this.visible.set(false);
    }
  }

  save() {
    this.visible.set(false);
  }

  generateApiKey() {
    this.loadingApiKeyGeneration = true;
    this._userService
      .addConsumerKey("Default Authentication", [], ["SEND_SMTP", "SEND_API"])
      .pipe(
        mergeMap((consumerKeyWithSecret) =>
          this._userService
            .disableTooltip("has_legacy_api_key")
            .pipe(map((_) => consumerKeyWithSecret)),
        ),
        catchError((err) => {
          this._messageService.add({
            severity: "error",
            summary: err.error.message,
          });
          return throwError(err);
        }),
        finalize(() => {
          this.loadingApiKeyGeneration = false;
        }),
      )
      .subscribe((response) => {
        this.generatedApiKey = response;
      });
  }

  closeModalAndDisableTooltip() {
    this._userService.disableTooltip("has_legacy_api_key").subscribe(() => {
      this.visible.set(false);
    });
  }

  copyToClipboard(text: string) {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        this._messageService.add({
          severity: "success",
          summary: this._translateService.translate("copied_to_clipboard"),
        });
      })
      .catch(() => {
        this._messageService.add({
          severity: "error",
          summary: this._translateService.translate("copy_failed"),
        });
      });
  }
}
